import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { format, startOfDay } from "date-fns";
import { de } from "date-fns/locale";
import { useLanguage } from "@/hooks/useLanguage";
import { Calendar as CalendarIcon, Trash2, RefreshCw, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface AppointmentManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitId: string;
  currentAppointment: {
    date: Date;
    notes?: string;
  };
  onAppointmentChanged: () => void;
}

export const AppointmentManagementDialog = ({
  open,
  onOpenChange,
  unitId,
  currentAppointment,
  onAppointmentChanged,
}: AppointmentManagementDialogProps) => {
  const { getLocale, isHoliday } = useLanguage();
  const { toast } = useToast();
  const [action, setAction] = useState<'cancel' | 'reschedule' | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [cancelReason, setCancelReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const generateTimeSlots = () => {
    const slots: string[] = [];
    const isSaturday = selectedDate && selectedDate.getDay() === 6;
    
    if (isSaturday) {
      for (let hour = 10; hour <= 16; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          if (hour === 16 && minute > 15) break;
          slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
        }
      }
    } else {
      for (let hour = 10; hour <= 13; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          if (hour === 13 && minute > 0) break;
          slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
        }
      }
      
      for (let hour = 15; hour <= 19; hour++) {
        const startMinute = hour === 15 ? 30 : 0;
        for (let minute = startMinute; minute < 60; minute += 15) {
          if (hour === 19 && minute > 30) break;
          slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
        }
      }
    }
    
    return slots;
  };

  const handleCancel = async () => {
    setShowCancelConfirm(true);
  };

  const confirmCancel = async () => {
    setLoading(true);
    try {
      // Send cancellation email
      const { error: emailError } = await supabase.functions.invoke('cancel-appointment-email', {
        body: {
          unit_id: unitId,
          reason: cancelReason.trim() || undefined
        }
      });

      if (emailError) {
        console.error('Error sending cancellation email:', emailError);
        toast({
          title: "Warnung",
          description: "Termin wurde abgesagt, aber E-Mail konnte nicht gesendet werden",
          variant: "default"
        });
      } else {
        toast({
          title: "Termin abgesagt",
          description: "Absage-E-Mails wurden versendet",
          variant: "default"
        });
      }

      // Clear the appointment (done by edge function)
      onAppointmentChanged();
      onOpenChange(false);
    } catch (error) {
      console.error('Error canceling appointment:', error);
      toast({
        title: "Fehler",
        description: "Termin konnte nicht abgesagt werden",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedDate) return;
    
    setLoading(true);
    try {
      const [hours, minutes] = selectedTime.split(':');
      const newDate = new Date(selectedDate);
      newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

      // Update appointment
      const { error: updateError } = await supabase
        .from('d2d_units')
        .update({
          next_reminder: newDate.toISOString(),
          manual_reminder: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', unitId);

      if (updateError) throw updateError;

      // Send reschedule email
      const { error: emailError } = await supabase.functions.invoke('reschedule-appointment-email', {
        body: {
          unit_id: unitId,
          old_date: currentAppointment.date.toISOString(),
          new_date: newDate.toISOString()
        }
      });

      if (emailError) {
        console.error('Error sending reschedule email:', emailError);
        toast({
          title: "Termin verschoben",
          description: "Termin wurde verschoben, aber E-Mail konnte nicht gesendet werden",
          variant: "default"
        });
      } else {
        toast({
          title: "Termin verschoben",
          description: "Änderungs-E-Mails wurden versendet",
          variant: "default"
        });
      }

      onAppointmentChanged();
      onOpenChange(false);
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast({
        title: "Fehler",
        description: "Termin konnte nicht verschoben werden",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              Termin verwalten
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Aktueller Termin: {format(currentAppointment.date, 'EEEE, dd. MMMM yyyy - HH:mm', { locale: de })} Uhr
            </p>

          {!action && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Was möchten Sie tun?</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => setAction('reschedule')}
                  className="h-20 flex flex-col items-center justify-center gap-2 text-sm font-medium border-primary/20 hover:border-primary hover:bg-primary/5"
                >
                  <RefreshCw className="h-5 w-5 text-primary" />
                  <span className="text-center">Verschieben</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setAction('cancel')}
                  className="h-20 flex flex-col items-center justify-center gap-2 text-sm font-medium border-destructive/20 hover:border-destructive hover:bg-destructive/5"
                >
                  <Trash2 className="h-5 w-5 text-destructive" />
                  <span className="text-center">Absagen</span>
                </Button>
              </div>
            </div>
          )}

          {action === 'cancel' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Termin absagen</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Der Kunde und Sie erhalten eine Absage-E-Mail. Der Follow-up wird aus dem System entfernt.
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Grund der Absage (optional)</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Z.B. Krankheit, dringende Termine..."
                  className="mt-2 min-h-[100px]"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setAction(null)}
                  className="flex-1"
                  disabled={loading}
                >
                  Zurück
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleCancel}
                  className="flex-1"
                  disabled={loading}
                >
                  {loading ? "Wird abgesagt..." : "Termin absagen"}
                </Button>
              </div>
            </div>
          )}

          {action === 'reschedule' && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Neues Datum wählen</Label>
                <div className="bg-card rounded-lg p-2 border mt-2 flex justify-center">
                  <Calendar 
                    mode="single" 
                    selected={selectedDate} 
                    onSelect={setSelectedDate}
                    disabled={(date) => date < startOfDay(new Date()) || isHoliday(date)}
                    initialFocus 
                    className="pointer-events-auto"
                    locale={de}
                  />
                </div>
              </div>

              {selectedDate && (
                <div>
                  <Label className="text-sm font-medium">Zeit wählen</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {generateTimeSlots().map((time) => (
                      <Button
                        key={time}
                        variant={selectedTime === time ? 'default' : 'outline'}
                        onClick={() => setSelectedTime(time)}
                        className="text-sm"
                      >
                        {time}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setAction(null)}
                  className="flex-1"
                  disabled={loading}
                >
                  Zurück
                </Button>
                <Button
                  onClick={handleReschedule}
                  className="flex-1"
                  disabled={!selectedDate || loading}
                >
                  {loading ? "Wird verschoben..." : "Termin verschieben"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Termin wirklich absagen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Termin wird abgesagt und der Kunde sowie Sie erhalten eine Absage-E-Mail. 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Ja, absagen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
